/**
 * `ExpauseAppBar` — the shared inline page app bar.
 *
 * Web port of Flutter's reusable page bar `BaseAppBar`
 * (`lib/presentation/custom_widgets/base_app_bar.dart:5-42`): a leading back
 * control (Flutter's `GestureDetector` around the back chevron, `:21-29`), a
 * `centerTitle: true` single-line title (`:31-36`) and a fixed `kToolbarHeight`
 * box (`:41`). The translucent blurred backdrop and the title-less state come
 * from Flutter's content app-bar variant
 * (`lib/presentation/screens/content/custom_widgets/scrollable/content_list_widget.dart:1112-1147`
 * — a `.6`-alpha background behind a `BackdropFilter(ImageFilter.blur(…))`,
 * with an empty `title: Container()`). The trailing `actions` slot is the web
 * superset of Flutter's raw `AppBar(actions: …)` call sites (e.g. the community
 * screen's share button), which `BaseAppBar` itself does not expose.
 *
 * The centered title is **static**: this component attaches no scroll listener
 * and exposes no opacity props. Scroll-driven title fading stays entirely
 * `FlexibleHeaderList`'s internal business.
 *
 * Scope note: this bar is deliberately *modelled on* — but shares **no code**
 * with — `FlexibleHeaderList`'s internal title bar (same three-cell
 * leading/title/actions layout, same `calc(topBar.height + safeArea.top)` box,
 * same blurred translucent backdrop, same stacking band). The two
 * implementations coexist by an explicit story-level scope decision, so that
 * giving the hand-rolled page bars a shared component cannot regress the
 * sixteen shipped `FlexibleHeaderList` list pages; a later branch may unify
 * them.
 *
 * Styling follows web conventions (theme tokens + `responsive.*`), which
 * `presentation.md` → "Web vs Flutter UI parity scope" permits to deviate from
 * Flutter's raw values.
 */

import { forwardRef, type ReactNode } from 'react';

import { BackIconButton } from '@/presentation/components/common/BackIconButton';
import { APP_BAR_CONSTANTS } from '@/presentation/constants/appBarConstants';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';

/** `#RRGGBB` → `#RRGGBBAA` suffix for the bar's translucent background fill. */
const BACKGROUND_ALPHA_HEX = Math.round(APP_BAR_CONSTANTS.backgroundOpacity * 255)
  .toString(16)
  .padStart(2, '0');

export interface ExpauseAppBarProps {
  /**
   * `data-qa-*` for the bar's **root** element — the handle a UI test uses to
   * reach the bar box directly instead of walking up from the leading button to
   * the nearest sticky ancestor (a walk that resolves the *wrong* element on a
   * `sticky={false}` bar such as `CommunityAppBar`).
   *
   * Opt-in with **no default**: omitted → the root emits no `data-qa-*`. Pass a
   * feature-prefixed, page-unique id (`{ id: 'legal-app-bar' }`), never a bare
   * `'app-bar'` (`qaAttr.ts` → id-naming convention). A bare id would collide
   * across pages because `KeepAliveOutlet` keeps every cached route **mounted**
   * and only toggles `display` (`RouteStack.tsx`), so several bars sit in the
   * document at once and an unscoped `querySelector` would return whichever
   * hidden one comes first in DOM order. Even a page-unique id can appear twice
   * (the same page cached under two param sets, e.g. two legal documents), so a
   * query still scopes with `[data-route-active="true"]`.
   */
  rootQa?: QaAttrOptions;
  /**
   * Centered title text. Omitted → the centering cell is still rendered (so the
   * bar keeps its three-cell layout) but as a plain `<div>`, **not** a heading:
   * an empty `<h1>` is an `empty-heading` a11y violation, and on the title-less
   * media viewers it would be the document's only `h1`. Flutter parity:
   * `title: Container()` (`content_list_widget.dart:1126`).
   */
  title?: string;
  /**
   * When set, the title becomes an `<h1>` **wrapping** a `<button>` carrying
   * this handler (Flutter `community_screen.dart`'s `appBarTitle()`
   * `GestureDetector.onTap` → `onBackPress()`). The nesting order is
   * load-bearing and must not be inverted: `<button>` takes phrasing content
   * only, so an `<h1>` inside it is an invalid content model.
   */
  onTitleTap?: () => void;
  /**
   * `data-qa-*` for the centered title cell — the `<button>` (the interactive
   * element inside the `<h1>`) when `onTitleTap` is set, the `<h1>` when only
   * `title` is set, and the non-heading `<div>` cell in the title-less state.
   *
   * Opt-in with **no default**, under the same feature-prefixed / page-unique
   * rule as `rootQa`; `CommunityAppBar` passes `{ id: 'community-title' }`.
   * Most pages need nothing here: with `rootQa` set, a test reaches the title
   * as a descendant of the bar root.
   */
  titleQa?: QaAttrOptions;
  /** Tap handler for the leading `BackIconButton`. */
  onLeadingTap?: () => void;
  /**
   * `data-qa-*` forwarded to the `BackIconButton`. Omitted → `BackIconButton`'s
   * own default (`back-button`).
   */
  leadingQa?: QaAttrOptions;
  /** Trailing action node(s), right-aligned. */
  actions?: ReactNode;
  /**
   * Pin the bar with `position: sticky; top: 0` at `APP_BAR_CONSTANTS.zIndex`.
   * Default `true`; pass `false` when the caller already owns a sticky wrapper.
   */
  sticky?: boolean;
}

/**
 * Forwarded `ref` → the bar's ROOT element (the sticky box itself).
 *
 * Exists so a page that has to know how much of its scroll viewport the bar
 * covers can measure it (`useMeasuredHeight` → `VirtualList.scrollOffsetTop`,
 * so an `initialIndex` landing does not arrive behind the bar).
 *
 * Deliberately a forwarded ref rather than a `<div ref>` WRAPPER around the
 * bar: a wrapper is a block box exactly as tall as the bar, and a
 * `position: sticky` element can never travel outside its own parent's box, so
 * wrapping would silently turn the sticky bar into a bar that scrolls away.
 * The height read here is the rendered one, which already resolves the
 * `env(safe-area-inset-top)` term of the bar's `calc()` height — a unit-bearing
 * string that cannot be recomputed as a number in JS.
 */
export const ExpauseAppBar = forwardRef<HTMLDivElement, ExpauseAppBarProps>(({
  rootQa,
  title,
  onTitleTap,
  titleQa,
  onLeadingTap,
  leadingQa,
  actions,
  sticky = true,
}, ref) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const titleStyle = {
    margin: 0,
    flexShrink: 1,
    textAlign: 'center' as const,
    fontWeight: responsive.fontWeight.bold,
    fontSize: responsive.font.xl,
    color: theme.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  };

  return (
    <div
      ref={ref}
      {...(rootQa ? qaAttr(rootQa) : {})}
      style={{
        // iOS PWA: extend the bar behind the status bar and push its content
        // below it via paddingTop. The blur backdrop fills the full area
        // (status bar included), so the visual is one continuous bar — the same
        // box `FlexibleHeaderList`'s bar builds.
        height: `calc(${responsive.topBar.height}px + ${responsive.safeArea.top})`,
        paddingTop: responsive.safeArea.top,
        // Required by the `display:flex; flexDirection:column; height:100vh`
        // page shells: without it the bar squashes when the body overflows.
        flexShrink: 0,
        // Stacking band lives on `APP_BAR_CONSTANTS.zIndex` (see that token for
        // the cross-file overlay-order contract it encodes). When the caller
        // owns the sticky wrapper the root still needs to be positioned,
        // otherwise the `inset: 0` backdrop below escapes to some outer
        // ancestor's box.
        ...(sticky
          ? { position: 'sticky' as const, top: 0, zIndex: APP_BAR_CONSTANTS.zIndex }
          : { position: 'relative' as const }),
      }}
    >
      {/* Blur backdrop — translucent fill over the scrolling body. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: theme.background + BACKGROUND_ALPHA_HEX,
          backdropFilter: `blur(${responsive.appBar.backdropBlur}px)`,
          WebkitBackdropFilter: `blur(${responsive.appBar.backdropBlur}px)`,
        }}
      />

      {/* Bar content — leading / title / actions. */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          height: responsive.topBar.height,
          paddingLeft: responsive.spacing.sm,
          paddingRight: responsive.spacing.sm,
          gap: responsive.gap.sm,
        }}
      >
        {/* Leading */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start', flexShrink: 0 }}>
          <BackIconButton onClick={onLeadingTap} {...(leadingQa ? { qa: leadingQa } : {})} />
        </div>

        {/* Title — static (no scroll-driven fade); no heading in the title-less state. */}
        {onTitleTap ? (
          // The heading wraps the button, never the other way round: `<button>`
          // takes phrasing content only, so an `<h1>` child is an invalid
          // content model. Heading-wraps-control is the standard accessible
          // pattern and keeps the whole title text as the tap target (Flutter
          // `community_screen.dart`'s `appBarTitle()` `GestureDetector`).
          // `titleQa` stays on the interactive element, exactly where it sat
          // before, so no QA selector moves.
          <h1 style={titleStyle}>
            <button
              {...(titleQa ? qaAttr(titleQa) : {})}
              onClick={onTitleTap}
              style={{
                // `display: block` + `maxWidth: 100%` reproduce the ellipsis
                // behaviour the previous flex-button/`<span>` pair had: the
                // button is now the element whose text can overflow, so it
                // carries its own overflow/ellipsis triple (`titleStyle` keeps
                // its copies on the `<h1>` harmlessly).
                display: 'block',
                maxWidth: '100%',
                background: 'none',
                border: 'none',
                padding: 0,
                margin: 0,
                cursor: 'pointer',
                // Buttons do not inherit typography — keep the heading's.
                font: 'inherit',
                color: 'inherit',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap' as const,
              }}
            >
              {title}
            </button>
          </h1>
        ) : title ? (
          <h1 {...(titleQa ? qaAttr(titleQa) : {})} style={titleStyle}>
            {title}
          </h1>
        ) : (
          // Title-less state (Flutter `title: Container()`,
          // `content_list_widget.dart:1126`). An empty `<h1>` would be an
          // `empty-heading` a11y violation — and on the media viewers it would
          // be the document's only `h1` — so the centering cell renders as a
          // plain `<div>` and keeps carrying the qa handle.
          <div {...(titleQa ? qaAttr(titleQa) : {})} style={titleStyle} />
        )}

        {/* Actions */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0,
            // Same padding the leading back button applies on the left.
            paddingRight: responsive.spacing.sm,
          }}
        >
          {actions}
        </div>
      </div>
    </div>
  );
});

ExpauseAppBar.displayName = 'ExpauseAppBar';
